import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { PropertyReportWithStats, DashboardStats } from '../../types/models';
import { FirebaseService } from '../../services/FirebaseService';
import { ReportsGrid } from './MyReports/ReportsGrid';
import { SearchFilter } from './MyReports/SearchFilter';
import { ReportViewer } from '../ReportViewer';
import { Sidebar } from './Sidebar';
import { Settings } from './Account/Settings';
import SharedReports from './Shared/SharedReports';
import './Dashboard.css';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<PropertyReportWithStats[]>([]);
  const [filteredReports, setFilteredReports] = useState<PropertyReportWithStats[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'dashboard' | 'report-viewer'>('dashboard');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sharedViewMode, setSharedViewMode] = useState<'reports' | 'company-images'>('reports');
  const [isViewingSharedReport, setIsViewingSharedReport] = useState(false);

  // Reset shared view mode when changing tabs
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab !== 'shared') {
      setSharedViewMode('reports');
    }
  };

  const fetchReports = useCallback(async () => {
    if (!user) return;

    setError(null);
    try {
      // console.log('Fetching reports for user:', user.uid);
      
      // Use new FirebaseService with nested collections
      const reportsData = await FirebaseService.fetchReports(user.uid);

      // Fetch entry data for each report to calculate stats
      const reportsWithStats: PropertyReportWithStats[] = await Promise.all(
        reportsData.map(async (report) => {
          try {
            const entries = await FirebaseService.fetchEntries(user.uid, report.id);
            const imageCount = entries.reduce((total, entry) => {
              return total + (entry.images?.length || 0) + (entry.photoURL ? 1 : 0);
            }, 0);
            
            return {
              ...report,
              entryCount: entries.length,
              imageCount: imageCount
            };
          } catch (error) {
            // console.warn(`Failed to fetch entries for report ${report.id}:`, error);
            return {
              ...report,
              entryCount: 0,
              imageCount: 0
            };
          }
        })
      );

      // console.log('Reports with stats loaded:', reportsWithStats.length);
      setReports(reportsWithStats);
      setFilteredReports(reportsWithStats);
      calculateStats(reportsWithStats);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError(`Failed to load reports: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchReports();
    }
  }, [user, fetchReports]);

  const calculateStats = (reportsData: PropertyReportWithStats[]) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const thisMonthReports = reportsData.filter(
      report => report.createdAt >= startOfMonth
    ).length;

    const recentActivity = reportsData
      .slice(0, 5)
      .map(report => ({
        id: report.id,
        type: 'report_created' as const,
        reportTitle: report.title,
        timestamp: report.createdAt,
      }));

    // Stats calculation code removed as variables were unused
  };

  const handleFilter = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
    } else {
      const filtered = reports.filter(report =>
        report.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredReports(filtered);
    }
  };

  const handleSort = (sortBy: 'title' | 'createdAt' | 'updatedAt', sortOrder: 'asc' | 'desc') => {
    const sorted = [...filteredReports].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      if (sortBy === 'title') {
        aValue = a.title;
        bValue = b.title;
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredReports(sorted);
  };

  const handleViewReport = (reportId: string) => {
    setSelectedReportId(reportId);
    setIsViewingSharedReport(false);
    setViewMode('report-viewer');
  };

  const handleViewSharedReport = (reportId: string) => {
    console.log('🔍 Dashboard: Viewing shared report:', reportId);
    setSelectedReportId(reportId);
    setIsViewingSharedReport(true);
    setViewMode('report-viewer');
  };

  const handleViewPDF = (reportId: string) => {
    navigate(`/pdf-editor/${reportId}`);
  };

  const handleViewSharedPDF = (reportId: string) => {
    // Navigate to PDF editor for shared reports with shared flag
    navigate(`/pdf-editor/${reportId}?shared=true`);
  };

  const handleBackToDashboard = () => {
    setSelectedReportId(null);
    setViewMode('dashboard');
    setIsViewingSharedReport(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Show different views based on viewMode
  if (selectedReportId && viewMode === 'report-viewer') {
    return (
      <div className="app-layout">
        <Sidebar 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          isOpen={sidebarOpen}
          onClose={closeSidebar}
        />
        <div className="main-content">
          <ReportViewer 
            reportId={selectedReportId} 
            onBack={handleBackToDashboard}
            onViewPDF={handleViewPDF}
            onToggleSidebar={toggleSidebar}
            isSharedReport={isViewingSharedReport}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-layout">
        <Sidebar 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          isOpen={sidebarOpen}
          onClose={closeSidebar}
        />
        <div className="main-content">
          <div className="dashboard-header">
            <button className="mobile-menu-toggle" onClick={toggleSidebar}>
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h1 className="dashboard-title">Error</h1>
          </div>
          <div className="dashboard-error">
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={fetchReports} className="btn btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderMainContent = () => {
    switch (activeTab) {
      case 'settings':
        return (
          <div className="dashboard">
            <div className="dashboard-header">
              <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                <span></span>
                <span></span>
                <span></span>
              </button>
              <h1 className="dashboard-title">Settings</h1>
            </div>
            <Settings />
          </div>
        );
      case 'shared':
        return (
          <div className="dashboard">
            {sharedViewMode === 'reports' && (
              <div className="dashboard-header">
                <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
                <h1 className="dashboard-title">Shared Reports</h1>
              </div>
            )}
            {sharedViewMode === 'company-images' && (
              <div className="report-viewer-header">
                <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
            )}
            <SharedReports 
              onViewReport={handleViewSharedReport}
              onViewPDF={handleViewSharedPDF}
              viewMode={sharedViewMode}
              onViewModeChange={setSharedViewMode}
            />
          </div>
        );
      case 'dashboard':
      default:
        return (
          <div className="dashboard">
            <div className="dashboard-header">
              <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                <span></span>
                <span></span>
                <span></span>
              </button>
              <h1 className="dashboard-title">My Reports</h1>
            </div>
            <div className="dashboard-content">
              <SearchFilter onFilter={handleFilter} onSort={handleSort} />
              <ReportsGrid 
                reports={filteredReports} 
                onViewReport={handleViewReport}
                onReportDeleted={fetchReports}
                onReportShared={fetchReports}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="app-layout">
      <Sidebar 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />
      <div className="main-content">
        {renderMainContent()}
      </div>
    </div>
  );
};
